import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { ESTADOS_COLOR } from "../../../utils/constants";
import * as clienteService from "../services/clienteService";
import TruncatedCellText from "./TruncatedCellText";

const SubHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[900]
    : theme.palette.grey[50],
  color: theme.palette.text.secondary,
}));

const DIALOG_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const CONTACTO_INI = {
  nombre_contacto: "",
  cargo_contacto: "",
  correo: "",
  telefono: "",
  celular: "",
  tipo_contacto: "Facturacion",
  estado: "Activo",
};

const TIPOS_CONTACTO_SERVICIO = ["Gerente General", "Supervisor del Servicio", "Cobranza", "Representante Legal", "Otro"];

const ContactosServicioDialog = ({ open, cliente, servicio, onClose }) => {
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(CONTACTO_INI);

  const cargarContactos = useCallback(async () => {
    if (!cliente?.cod_cliente || !servicio?.cod_servicio_cliente) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await clienteService.getContactosServicio(
        cliente.cod_cliente,
        servicio.cod_servicio_cliente,
      );
      setContactos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron cargar los contactos");
      setContactos([]);
    } finally {
      setLoading(false);
    }
  }, [cliente?.cod_cliente, servicio?.cod_servicio_cliente]);

  useEffect(() => {
    if (open) {
      setEditing(null);
      setForm(CONTACTO_INI);
      setFormError(null);
      cargarContactos();
    }
  }, [open, cargarContactos]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNew = () => {
    setEditing(null);
    setForm(CONTACTO_INI);
    setFormError(null);
  };

  const handleEdit = (contacto) => {
    setEditing(contacto);
    setForm({
      nombre_contacto: contacto.nombre_contacto || "",
      cargo_contacto: contacto.cargo_contacto || "",
      correo: contacto.correo || "",
      telefono: contacto.telefono || "",
      celular: contacto.celular || "",
      tipo_contacto: contacto.tipo_contacto || "Facturacion",
      estado: contacto.estado || "Activo",
    });
    setFormError(null);
  };

  const handleSaveContacto = async () => {
    if (!form.nombre_contacto.trim()) {
      setFormError("El nombre del contacto es requerido");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        ...form,
        nombre_contacto: form.nombre_contacto.trim(),
        cargo_contacto: form.cargo_contacto.trim(),
        correo: form.correo.trim(),
        telefono: form.telefono.trim(),
        celular: form.celular.trim(),
      };

      const contactoId = editing?.id ?? editing?.id_contacto;
      if (contactoId) {
        await clienteService.updateContactoServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
          contactoId,
          payload,
        );
      } else {
        await clienteService.createContactoServicio(
          cliente.cod_cliente,
          servicio.cod_servicio_cliente,
          payload,
        );
      }

      await cargarContactos();
      handleNew();
    } catch (err) {
      setFormError(err?.response?.data?.message || "No se pudo guardar el contacto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contactos - Servicio #{servicio.cod_servicio_cliente}</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ ...DIALOG_COMPACT_SX, mb: 1.5 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth required size="small" label="Nombre" name="nombre_contacto"
                value={form.nombre_contacto} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Cargo" name="cargo_contacto"
                value={form.cargo_contacto} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="Correo" name="correo" type="email"
                value={form.correo} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Telefono" name="telefono"
                value={form.telefono} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Celular" name="celular"
                value={form.celular} onChange={handleFormChange} disabled={saving} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField select fullWidth size="small" label="Tipo" name="tipo_contacto"
                value={form.tipo_contacto} onChange={handleFormChange} disabled={saving}>
                {TIPOS_CONTACTO_SERVICIO.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField select fullWidth size="small" label="Estado" name="estado"
                value={form.estado} onChange={handleFormChange} disabled={saving}>
                {Object.keys(ESTADOS_COLOR).map((estado) => (
                  <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }} sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button variant="outlined" size="small" onClick={handleNew} disabled={saving}
                startIcon={<AddIcon />} sx={{ minHeight: 32 }}>
                Nuevo
              </Button>
              <Button variant="contained" size="small" onClick={handleSaveContacto} disabled={saving}
                startIcon={saving ? <CircularProgress size={14} /> : null} sx={{ minHeight: 32 }}>
                {editing ? "Actualizar" : "Guardar"}
              </Button>
            </Grid>
          </Grid>
          {formError && <Alert severity="error" sx={{ mt: 1.5 }}>{formError}</Alert>}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Cargando contactos...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !contactos.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Sin contactos registrados para este servicio
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SubHeaderCell>Nombre</SubHeaderCell>
                  <SubHeaderCell>Cargo</SubHeaderCell>
                  <SubHeaderCell>Correo</SubHeaderCell>
                  <SubHeaderCell>Telefono</SubHeaderCell>
                  <SubHeaderCell>Celular</SubHeaderCell>
                  <SubHeaderCell>Tipo</SubHeaderCell>
                  <SubHeaderCell>Estado</SubHeaderCell>
                  <SubHeaderCell align="center">Accion</SubHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contactos.map((contacto) => (
                  <TableRow key={contacto.id ?? contacto.id_contacto} hover>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={contacto.nombre_contacto} maxWidth={190} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.cargo_contacto || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>
                      <TruncatedCellText value={contacto.correo} maxWidth={190} />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.telefono || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.celular || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem" }}>{contacto.tipo_contacto || "—"}</TableCell>
                    <TableCell>
                      <Chip label={contacto.estado || "—"} size="small"
                        color={ESTADOS_COLOR[contacto.estado] || "default"}
                        sx={{ fontSize: "0.68rem", height: 18 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar contacto">
                        <IconButton size="small" onClick={() => handleEdit(contacto)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactosServicioDialog;
